// src/features/data/entities-env-tab.tsx
import { Chip, DataTable, Kicker, StatusDot, StatusPill } from "@/components/iris";
import type { DataTableColumn, SortDirection } from "@/components/iris";
import { DateRangePicker, type IsoRange } from "@/components/ui/date-range-picker";
import { MultiSelectFilter } from "@/components/ui/multi-select-filter";
import { BulkBar } from "@/features/entities/bulk-bar";
import { useEntities, useEntity } from "@/hooks/use-data";
import type { EntityDetail, EntityRecord, Env } from "@/lib/types";
import { Search } from "lucide-react";
import { useState } from "react";
import { buildCountryOptions, entityCountHeadline } from "./entities-env-tab.helpers";
import { EntityDeleteDialog } from "./entity-delete-dialog";
import { EntityEditDialog, type EntityEditEntity, type EntityEditType } from "./entity-edit-dialog";
import { EntityGroupsCell } from "./entity-groups-cell";

// ─── Helpers ───

function statusVariant(status: string) {
  switch (status) {
    case "active":
      return "ok" as const;
    case "inactive":
      return "inactive" as const;
    case "deleted":
      return "err" as const;
    default:
      return "idle" as const;
  }
}

function riskColor(score: number | null): string {
  if (score == null) return "var(--iris-fg-5)";
  if (score >= 70) return "var(--iris-neon-pink)";
  if (score >= 40) return "var(--iris-neon-orange)";
  return "var(--iris-neon-cyan)";
}

function fmtDate(iso: string | null): string {
  if (!iso) return "\u2014";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// ─── Detail Panel ───

function EntityDetailPanel({
  entity,
  onEditRequested,
  onDeleteRequested,
}: {
  entity: EntityRecord;
  onEditRequested: () => void;
  onDeleteRequested: () => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: 16, fontSize: 12 }}>
      <div>
        <Kicker variant="muted" style={{ marginBottom: 4 }}>
          Name
        </Kicker>
        <div style={{ color: "var(--iris-fg-1)" }}>{entity.name ?? "\u2014"}</div>
      </div>

      <div>
        <Kicker variant="muted" style={{ marginBottom: 4 }}>
          Identifier
        </Kicker>
        <div
          style={{ fontFamily: "var(--iris-font-mono)", fontSize: 11, color: "var(--iris-fg-3)" }}
        >
          {entity.identifier ?? "\u2014"}
          {entity.identifier_type && (
            <span
              style={{
                marginLeft: 6,
                borderRadius: "var(--iris-r-sm)",
                background: "var(--iris-bg-4)",
                padding: "2px 6px",
                fontSize: 10,
              }}
            >
              {entity.identifier_type}
            </span>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <Kicker variant="muted" style={{ marginBottom: 4 }}>
            Platform
          </Kicker>
          <div>{entity.platform ?? "\u2014"}</div>
        </div>
        <div>
          <Kicker variant="muted" style={{ marginBottom: 4 }}>
            Status
          </Kicker>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <StatusDot variant={statusVariant(entity.status)} size={7} />
            {entity.status}
          </div>
        </div>
        <div>
          <Kicker variant="muted" style={{ marginBottom: 4 }}>
            Groups
          </Kicker>
          <div>{entity.group_count}</div>
        </div>
        <div>
          <Kicker variant="muted" style={{ marginBottom: 4 }}>
            Risk Score
          </Kicker>
          <div>{entity.risk_score ?? "\u2014"}</div>
        </div>
      </div>

      {entity.classifications.length > 0 && (
        <div>
          <Kicker variant="muted" style={{ marginBottom: 8 }}>
            Classifications
          </Kicker>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {entity.classifications.map((c) => (
              <Chip key={c} style={{ fontSize: 10, padding: "2px 8px" }}>
                {c}
              </Chip>
            ))}
          </div>
        </div>
      )}

      <div>
        <Kicker variant="muted" style={{ marginBottom: 4 }}>
          Last Active
        </Kicker>
        <div style={{ color: "var(--iris-fg-3)" }}>{fmtDate(entity.last_active_at)}</div>
      </div>

      <div
        style={{
          borderTop: "1px solid var(--iris-line-1)",
          paddingTop: 12,
          display: "flex",
          gap: 8,
          justifyContent: "flex-end",
        }}
      >
        <button type="button" className="iris-btn" onClick={onEditRequested}>
          Edit entity
        </button>
        <button type="button" className="iris-btn iris-btn--primary" onClick={onDeleteRequested}>
          Delete entity
        </button>
      </div>
    </div>
  );
}

// ─── Main ───

const PAGE_SIZE = 25;
const STATUS_OPTIONS = ["active", "inactive", "deleted"];
const PLATFORM_OPTIONS = ["telegram", "whatsapp", "signal", "other"];
// Column key → backend sort key (entities_list SORT_FIELDS_ENTITY). Identity
// for the four sortable columns; listed explicitly so unknown keys are ignored.
const SORT_KEY_MAP: Record<string, string> = {
  name: "name",
  risk_score: "risk_score",
  group_count: "group_count",
  status: "status",
};

export function EntitiesEnvTab() {
  const [env, setEnv] = useState<Env>("prod");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  // IR-1045 Phase 1: platform/status multi-select + server-side sort. The
  // entities_list endpoint comma-splits platform/status and accepts ?sort=.
  const [platformFilter, setPlatformFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  // IR-1415 (Sean): "date range - all entities that were added to the system in
  // a customized date range" + "countries drop list - filter only to numbers
  // from specific countries". Both bounds are inclusive whole days; the country
  // list is served by the endpoint (`country_options`) rather than hardcoded,
  // because it is real data (280 distinct codes on PROD) and carries counts.
  const [addedRange, setAddedRange] = useState<IsoRange>({});
  const [countryFilter, setCountryFilter] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [selected, setSelected] = useState<EntityRecord | null>(null);
  // IR-661 Rotem round-2 (2026-05-20): bulk-select + Edit/Delete wireup.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editTarget, setEditTarget] = useState<EntityRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EntityRecord | null>(null);

  const toggleSelectId = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allOnPageSelected = (rows: EntityRecord[]) =>
    rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const toggleSelectAllOnPage = (rows: EntityRecord[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (rows.every((r) => next.has(r.id))) {
        for (const r of rows) next.delete(r.id);
      } else {
        for (const r of rows) next.add(r.id);
      }
      return next;
    });
  };

  // NOTE: The backend endpoint /api/ops/v1/data/entities accepts ?env=dev|prod.
  // When useEntities is updated to accept an env param, replace with: useEntities({ ..., env }).
  const entityFilters: Parameters<typeof useEntities>[0] = { page, page_size: PAGE_SIZE };
  if (search) entityFilters.q = search;
  if (platformFilter.length > 0) entityFilters.platform = platformFilter.join(",");
  if (statusFilter.length > 0) entityFilters.status = statusFilter.join(",");
  if (countryFilter.length > 0) entityFilters.country = countryFilter.join(",");
  if (addedRange.from) entityFilters.date_from = addedRange.from;
  if (addedRange.to) entityFilters.date_to = addedRange.to;
  if (sortKey && sortDirection) {
    entityFilters.sort = `${sortDirection === "desc" ? "-" : ""}${SORT_KEY_MAP[sortKey]}`;
  }
  const { data, isLoading } = useEntities(entityFilters);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  // IR-1414: the headline led with `total`, which includes soft-deleted
  // entities — 238,190 of them on prod — while the IRIS dashboard counts only
  // live ones, so the two systems disagreed by exactly that many. Lead with the
  // live count and disclose the deleted rows beside it. Pagination keeps using
  // `total`: the table still lists deleted rows, so the page maths must too.
  //
  // IR-1415: the country and date filters MUST be part of `isFiltered`. The
  // facets the split is derived from describe the whole table, so decorating a
  // filtered total with a whole-table deleted count states a falsehood about
  // the current view — any new filter has to be added here too.
  const headline = entityCountHeadline(
    total,
    data?.facets?.status,
    Boolean(search) ||
      platformFilter.length > 0 ||
      statusFilter.length > 0 ||
      countryFilter.length > 0 ||
      Boolean(addedRange.from) ||
      Boolean(addedRange.to),
  );

  // The options are count-ordered by the backend, so the countries an analyst
  // actually has numbers for sit at the top of the list. Keep a selected code
  // visible even if it drops out of the served top-50, or clearing it becomes
  // impossible from the menu.
  const countryOptions = buildCountryOptions(data?.country_options, countryFilter);

  function resetPage() {
    setPage(1);
  }

  // Server-side sort: map the clicked column to a backend sort key, reset to
  // page 1 so the user sees the new top of the ordering.
  function handleSort(columnKey: string, direction: SortDirection) {
    setSortKey(direction ? columnKey : null);
    setSortDirection(direction);
    resetPage();
  }

  const columns: DataTableColumn<EntityRecord>[] = [
    {
      // IR-661 Rotem round-2: per-row + select-all-on-page checkbox for
      // BulkBar (Set Status / Set Classification / Delete Selected).
      // Mirrors the groups-unified-tab pattern.
      key: "__select",
      label: (
        <input
          type="checkbox"
          aria-label="Select all on page"
          checked={allOnPageSelected(items)}
          onChange={(e) => {
            e.stopPropagation();
            toggleSelectAllOnPage(items);
          }}
          onClick={(e) => e.stopPropagation()}
          style={{ accentColor: "var(--iris-neon-purple)" }}
        />
      ) as unknown as string,
      render: (entity) => (
        <input
          type="checkbox"
          aria-label={`Select ${entity.name ?? entity.identifier ?? entity.id}`}
          checked={selectedIds.has(entity.id)}
          onChange={(e) => {
            e.stopPropagation();
            toggleSelectId(entity.id);
          }}
          onClick={(e) => e.stopPropagation()}
          style={{ accentColor: "var(--iris-neon-purple)" }}
        />
      ),
    },
    {
      key: "name",
      label: "Entity",
      sortable: true,
      render: (entity) => (
        <span style={{ color: "var(--iris-fg-1)", fontWeight: 500 }}>
          {entity.name ?? "\u2014"}
        </span>
      ),
    },
    {
      // IR-1045 P3: identifier shown as-is (already the canonical intelligence
      // identifier per entity); fall back to name/id only when it's null.
      key: "identifier",
      label: "Identifier",
      cellVariant: "mono",
      render: (entity) => {
        const value = entity.identifier ?? entity.name ?? entity.id;
        const display = value.length > 18 ? `${value.slice(0, 16)}\u2026` : value;
        return (
          <span style={{ fontSize: 10 }} title={value}>
            {display}
          </span>
        );
      },
    },
    {
      // IR-1045 P3: surface the identifier type as its own column (was only
      // visible in the detail panel).
      key: "identifier_type",
      label: "Type",
      cellVariant: "muted",
      render: (entity) => (
        <span style={{ fontSize: 10 }}>{entity.identifier_type ?? "\u2014"}</span>
      ),
    },
    {
      key: "platform",
      label: "Platform",
      cellVariant: "muted",
      render: (entity) => <span>{entity.platform ?? "\u2014"}</span>,
    },
    {
      // IR-1045 P3: render ALL classifications as chips (was classifications[0]).
      key: "classification",
      label: "Classification",
      cellVariant: "muted",
      render: (entity) =>
        entity.classifications.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {entity.classifications.map((c) => (
              <Chip key={c} style={{ fontSize: 10, padding: "1px 6px" }}>
                {c}
              </Chip>
            ))}
          </div>
        ) : (
          <span style={{ color: "var(--iris-fg-5)" }}>{"\u2014"}</span>
        ),
    },
    {
      key: "risk_score",
      label: "Risk",
      sortable: true,
      headerAlign: "right",
      cellVariant: "num",
      render: (entity) =>
        entity.risk_score != null ? (
          <span style={{ color: riskColor(entity.risk_score), fontWeight: 600 }}>
            {entity.risk_score}
          </span>
        ) : (
          <span style={{ color: "var(--iris-fg-5)" }}>{"\u2014"}</span>
        ),
    },
    {
      // IR-1045 P3: the count is a tooltip trigger; hovering lazy-fetches the
      // group NAMES via the detail endpoint (list payload only has the count).
      key: "group_count",
      label: "Groups",
      sortable: true,
      cellVariant: "num",
      render: (entity) => <EntityGroupsCell entityId={entity.id} groupCount={entity.group_count} />,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (entity) => (
        <StatusPill variant={statusVariant(entity.status)}>{entity.status}</StatusPill>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", gap: 16 }}>
      {/* Main panel */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>
        {/* IR-1045 P3: prominent total — Rotem asked for the entity count to be
            a prominent figure by the title rather than a small toolbar meta. */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <span
            style={{
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1,
              color: "var(--iris-fg-1)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {isLoading ? "—" : headline.primary.toLocaleString()}
          </span>
          <span
            style={{
              fontSize: 12,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--iris-fg-4)",
            }}
          >
            Entities
          </span>
          {/* IR-1414: deleted rows are still in the table below (ops needs to
              see and restore them), so the count they contribute is stated
              rather than folded into the headline. Absent when there is
              nothing deleted, and under a filter — see entityCountHeadline. */}
          {!isLoading && headline.deleted !== null && (
            <span
              style={{ fontSize: 12, color: "var(--iris-fg-4)" }}
              title="Soft-deleted entities. They are listed in the table below and reachable via the Status filter, but they are not counted as live entities — the IRIS dashboard excludes them too."
            >
              · {headline.deleted.toLocaleString()} deleted
            </span>
          )}
        </div>

        {/* Toolbar */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <div style={{ position: "relative" }}>
            <Search
              size={12}
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--iris-fg-5)",
              }}
            />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                resetPage();
              }}
              placeholder="Search entities..."
              className="iris-input"
              style={{ paddingLeft: 28, width: 220 }}
            />
          </div>

          <MultiSelectFilter
            label="Platform"
            options={PLATFORM_OPTIONS.map((p) => ({ value: p, label: p }))}
            selected={platformFilter}
            onChange={(next) => {
              setPlatformFilter(next);
              resetPage();
            }}
          />

          <MultiSelectFilter
            label="Status"
            options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
            selected={statusFilter}
            onChange={(next) => {
              setStatusFilter(next);
              resetPage();
            }}
          />

          {/* IR-1415: countries drop list. MultiSelectFilter grows its own
              search row past 12 options (IR-1351 r5), which this list always
              exceeds. */}
          <MultiSelectFilter
            label="Country"
            options={countryOptions}
            selected={countryFilter}
            onChange={(next) => {
              setCountryFilter(next);
              resetPage();
            }}
          />

          {/* IR-1415: "added to the system" date range. Same picker the Join
              Links filter bar uses, so presets and the yyyy-MM-dd contract are
              shared. */}
          <div style={{ width: 200 }}>
            <DateRangePicker
              label="Added: any time"
              value={addedRange}
              onApply={(next) => {
                setAddedRange(next);
                resetPage();
              }}
            />
          </div>

          {/* Env toggle */}
          <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
            <Chip
              active={env === "dev"}
              onClick={() => {
                setEnv("dev");
                resetPage();
              }}
            >
              Dev
            </Chip>
            <Chip
              active={env === "prod"}
              onClick={() => {
                setEnv("prod");
                resetPage();
              }}
            >
              Prod
            </Chip>
          </div>
        </div>

        {/* Env context banner */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            borderRadius: "var(--iris-r-md)",
            border: `1px solid ${env === "prod" ? "rgba(168,85,247,0.2)" : "rgba(34,211,255,0.2)"}`,
            background: env === "prod" ? "rgba(168,85,247,0.05)" : "rgba(34,211,255,0.05)",
            padding: "8px 12px",
            fontSize: 11,
            color: env === "prod" ? "var(--iris-env-prod)" : "var(--iris-env-dev)",
          }}
        >
          <span style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            {env === "prod" ? "Production" : "Development"}
          </span>
          <span style={{ color: "var(--iris-fg-4)" }}>
            {"\u2014"} showing entities from {env} database
          </span>
        </div>

        {/* IR-661 Rotem round-2: bulk-action bar appears when any rows
            are selected. Mirrors groups-unified-tab. Entities have no
            items-sweep so no onJobEnqueued is wired. */}
        {selectedIds.size > 0 && (
          <BulkBar
            selectedCount={selectedIds.size}
            selectedIds={[...selectedIds]}
            selectedNames={items
              .filter((r) => selectedIds.has(r.id))
              .map((r) => r.name ?? r.identifier ?? r.id)}
            type="entities"
            onClear={() => setSelectedIds(new Set())}
          />
        )}

        {/* Table */}
        <div className="iris-card" style={{ padding: 0, overflow: "hidden" }}>
          {isLoading ? (
            <div style={{ padding: "16px 18px", color: "var(--iris-fg-3)", fontSize: 12 }}>
              Loading...
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={items}
              rowKey={(entity) => entity.id}
              onSort={handleSort}
              sortKey={sortKey}
              sortDirection={sortDirection}
              emptyMessage="No entities found"
            />
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 6,
              fontSize: 12,
              color: "var(--iris-fg-4)",
            }}
          >
            <span>
              Showing {(page - 1) * PAGE_SIZE + 1}&ndash;{Math.min(page * PAGE_SIZE, total)} of{" "}
              {total.toLocaleString()}
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="iris-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <button
                type="button"
                className="iris-btn iris-btn--primary"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail side panel */}
      {selected && (
        <div className="iris-card" style={{ width: 280, flexShrink: 0, overflow: "hidden" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid var(--iris-line-1)",
              padding: "12px 16px",
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--iris-fg-1)" }}>
              Entity Detail
            </span>
            <button
              type="button"
              onClick={() => setSelected(null)}
              style={{
                color: "var(--iris-fg-4)",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
            >
              {"\u2715"}
            </button>
          </div>
          <EntityDetailPanel
            entity={selected}
            onEditRequested={() => setEditTarget(selected)}
            onDeleteRequested={() => setDeleteTarget(selected)}
          />
        </div>
      )}

      {editTarget ? (
        <EntityEditGate
          entityId={editTarget.id}
          fallback={editTarget}
          onClose={() => setEditTarget(null)}
        />
      ) : null}

      {deleteTarget ? (
        <EntityDeleteDialog
          entity={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            setSelected((prev) => (prev?.id === deleteTarget.id ? null : prev));
          }}
        />
      ) : null}
    </div>
  );
}

// IR-661 Rotem round-2: the EntityEditDialog needs `type`/`app_type`/
// `appdata`/`system_update_time` — none of which the list payload
// carries. Gate the dialog on a useEntity() detail fetch and only open
// once the appdata is in hand. While loading, the dialog stays mounted
// against the bare-record fallback so the env-radio + name field paint
// immediately and feel responsive.
function EntityEditGate({
  entityId,
  fallback,
  onClose,
}: {
  entityId: string;
  fallback: EntityRecord;
  onClose: () => void;
}) {
  const { data } = useEntity(entityId);
  const source: EntityRecord = data ?? fallback;
  const detail = data as EntityDetail | undefined;
  const editType: EntityEditType = isEditableType(source.type) ? source.type : "im_account";
  const entity: EntityEditEntity = {
    ...source,
    type: editType,
    app_type: source.platform ?? null,
    appdata: detail?.appdata ?? {},
  };
  const sut = source.system_update_time;
  return sut ? (
    <EntityEditDialog entity={entity} system_update_time={sut} onClose={onClose} />
  ) : (
    <EntityEditDialog entity={entity} onClose={onClose} />
  );
}

function isEditableType(t: string | null | undefined): t is EntityEditType {
  return t === "phone" || t === "im_account" || t === "social_media_account" || t === "account";
}
